import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import propTypes from 'prop-types';
import {
  BookmarkCheck,
  ChevronLeft,
  Download,
  TriangleAlert
} from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import AgencyMultiSelectBar from '../AgencyMultiSelectBar';
import usePendingAppointments from './usePendingAppointments';
import AgencyAppointmentsFilters from './AgencyAppointmentsFilters';
import AgencyAppointmentsQuickFilters from './AgencyAppointmentsQuickFilters';
import AgencyPendingAgentCard from './AgencyPendingAgentCard';
import AgencyRecommendationsSection from './AgencyRecommendationsSection';
import AgencyUntappedSection from './AgencyUntappedSection';
import ExportConfirmedAppointmentsModal from './ExportConfirmedAppointmentsModal';
import ExportPendingAppointmentsModal from './ExportPendingAppointmentsModal';
import useUntappedAgents from './useUntappedAgents';
import { listUntappedAgents } from './untappedAgentsHelpers';
import useAppliedAgencySelection from 'hooks/useAppliedAgencySelection';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import {
  getStalledDaysSinceInProgress,
  isAppointmentActionable,
  STALL_DAYS_RED_GTE
} from 'helpers/appointments/appointments';
import useTabUrlState from 'hooks/useTabUrlState';
import { usePortalWalkthrough } from 'tailwind-components/PortalWalkthrough/PortalWalkthroughContext';
import { isMobileWebView } from 'helpers/mobileWebView';

const RECOMMENDATIONS_FILTER_ID = 'recommendations';
const UNTAPPED_FILTER_ID = 'untapped';

const AGENCY_APPOINTMENTS_QUICK_FILTERS = [
  {
    id: 'attention',
    label: 'Needs Attention',
    match: appt =>
      isAppointmentActionable({
        appointment_status: appt.Appointment_Status,
        substatus: appt.Substatus
      })
  },
  {
    id: 'stalled',
    label: 'Stalled',
    match: appt =>
      typeof appt.Stalled_Days === 'number' &&
      appt.Stalled_Days >= STALL_DAYS_RED_GTE
  },
  {
    id: 'all',
    label: 'All Open',
    match: () => true
  },
  {
    id: UNTAPPED_FILTER_ID,
    label: 'Untapped',
    match: () => false
  },
  {
    id: RECOMMENDATIONS_FILTER_ID,
    label: 'Recommendations',
    icon: <BookmarkCheck />,
    match: () => false
  }
];

const QUICK_FILTER_TABS = AGENCY_APPOINTMENTS_QUICK_FILTERS.map(
  ({ id, label, icon }) => ({ id, label, icon })
);

const QUICK_FILTER_TAB_IDS = QUICK_FILTER_TABS.map(({ id }) => id);

const PENDING_EXPORT_HEADERS = [
  'Ticket_ID',
  'Agent_Name',
  // row key is the client-side tag; CSV_HEADER_LABELS prints it as Agency_Name
  'agencyName',
  'NPN',
  'Carrier_Name',
  'Appointment_Type',
  'States',
  'Substatus'
];

const toAgencies = opts =>
  opts.map(o => ({ agencyId: String(o.value), agencyName: o.name }));

function pickQuickFilterIdByRecordPriority(countsById) {
  for (const { id } of AGENCY_APPOINTMENTS_QUICK_FILTERS) {
    if (id === RECOMMENDATIONS_FILTER_ID || id === UNTAPPED_FILTER_ID) {
      continue;
    }
    if ((countsById[id] ?? 0) > 0) return id;
  }
  return AGENCY_APPOINTMENTS_QUICK_FILTERS[0].id;
}

const AgencyAppointments = ({
  setLocalLoader,
  downloadAppointmentsReport,
  agencyData,
  getSpecificAgencyData
}) => {
  const navigate = useNavigate();
  const { id: routeAgencyId } = useParams();
  const noopSetLocalLoader = useCallback(() => {}, []);

  const [selectedAgencyOptions, setSelectedAgencyOptions] = useState([]);
  const { appliedOptions, isDirty, apply, markApplied } =
    useAppliedAgencySelection(selectedAgencyOptions);
  const appliedAgencies = useMemo(
    () => toAgencies(appliedOptions),
    [appliedOptions]
  );
  const appliedAgencyIds = useMemo(
    () => appliedAgencies.map(a => a.agencyId),
    [appliedAgencies]
  );
  const [agencyFilterSelection, setAgencyFilterSelection] = useState([]);
  const [timesInputClosed, setTimesInputClosed] = useState(0);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportPendingModalOpen, setExportPendingModalOpen] = useState(false);

  const [loadingConfirmedAppointments, setLoadingConfirmedAppointments] =
    useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [searchParams] = useSearchParams();
  // when the URL already names a tab, the smart default below must not override it
  const hasExplicitQuickFilter = Boolean(searchParams.get('tab'));
  // writeDefaultToUrl=false: the smart default (pickQuickFilterIdByRecordPriority) owns the
  // first write, so we don't pin "attention" in the URL before the records are counted
  const [quickFilter, setQuickFilter] = useTabUrlState({
    param: 'tab',
    tabIds: QUICK_FILTER_TAB_IDS,
    defaultTabId: AGENCY_APPOINTMENTS_QUICK_FILTERS[0].id,
    writeDefaultToUrl: false,
    // static tab list → scrub a typo'd/unknown ?tab= back to the default instead of leaving it
    correctInvalidToDefault: true
  });
  const quickFilterAutoAppliedAgencyIdRef = useRef(null);

  // both lists are keyed by the applied selection, so applying a new one is what
  // refetches them — there is no imperative fetch call left to keep in sync
  const {
    pendingAppointments,
    loadingPendingAppointments,
    failedAgencies: pendingFailedAgencies,
    retryFailed: retryPendingFailed,
    fetchPendingAppointments,
    exportPendingAppointments
  } = usePendingAppointments(appliedAgencies);

  const {
    untappedAgents,
    loadingUntappedAgents,
    failedAgencies: untappedFailedAgencies,
    retryFailed: retryUntappedFailed,
    removeUntappedAgent
  } = useUntappedAgents(appliedAgencies);

  const agencyOptions = useMemo(() => {
    const ai = agencyData?.agencyInformation;
    if (!ai) return [];
    const downlines = Array.isArray(ai.Downline_Agencies_Three)
      ? ai.Downline_Agencies_Three
      : [];
    return [ai, ...downlines].map(a => ({
      name: a?.name?._value ?? '',
      value: String(a?.id?._value ?? '')
    }));
  }, [agencyData]);

  const metaReady = useMemo(() => {
    const ai = agencyData?.agencyInformation;
    return !!ai && String(ai.id?._value ?? '') === String(routeAgencyId ?? '');
  }, [agencyData, routeAgencyId]);

  const agencyMetaLoading = Boolean(routeAgencyId) && !metaReady;

  // the route's initial selection is applied from an effect, so for one frame the
  // meta is ready and no query has started yet. that is still loading, not an empty
  // result — the Update button cannot apply an empty selection, so a missing applied
  // selection always means "not applied yet".
  const awaitingInitialSelection =
    Boolean(routeAgencyId) && appliedAgencies.length === 0;
  const pendingLoading = loadingPendingAppointments || awaitingInitialSelection;
  const untappedLoading = loadingUntappedAgents || awaitingInitialSelection;

  const filterAgencyIds = useMemo(
    () =>
      agencyFilterSelection.length
        ? new Set(agencyFilterSelection.map(o => String(o.value)))
        : null,
    [agencyFilterSelection]
  );

  const initialLoadRef = useRef(null);

  const agentFilterOptions = useMemo(() => {
    const map = new Map();
    for (const appt of pendingAppointments) {
      const rawAgentId = appt.Agent_ID;
      const agentId =
        rawAgentId === undefined || rawAgentId === null
          ? ''
          : String(rawAgentId);
      if (!agentId) continue;
      const key = `${appt.agencyId || ''}::${agentId}`;
      if (map.has(key)) continue;
      map.set(key, {
        id: key,
        agentId,
        name: appt.Agent_Name?.trim() || agentId,
        agencyId: String(appt.agencyId ?? ''),
        agencyName: appt.agencyName ?? ''
      });
    }
    return [...map.values()].sort(
      (a, b) =>
        (a.agencyName || '').localeCompare(b.agencyName || '') ||
        a.name.localeCompare(b.name)
    );
  }, [pendingAppointments]);

  const carrierFilterOptions = useMemo(() => {
    const set = new Set(
      pendingAppointments.map(a => a.Carrier_Name).filter(Boolean)
    );
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [pendingAppointments]);

  const substatusFilterOptions = useMemo(() => {
    const set = new Set(
      pendingAppointments.map(a => a.Substatus).filter(Boolean)
    );
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [pendingAppointments]);

  const filteredPendingAppointments = useMemo(() => {
    const agentIds = new Set(selectedAgents.map(a => a.id));
    return pendingAppointments.filter(appt => {
      if (filterAgencyIds && !filterAgencyIds.has(String(appt.agencyId))) {
        return false;
      }
      if (
        agentIds.size &&
        !agentIds.has(`${appt.agencyId || ''}::${String(appt.Agent_ID)}`)
      ) {
        return false;
      }
      if (
        selectedCarriers.length > 0 &&
        !selectedCarriers.includes(appt.Carrier_Name)
      ) {
        return false;
      }
      if (
        selectedStatuses.length > 0 &&
        !selectedStatuses.includes(appt.Substatus)
      ) {
        return false;
      }
      return true;
    });
  }, [
    pendingAppointments,
    selectedAgents,
    selectedCarriers,
    selectedStatuses,
    filterAgencyIds
  ]);

  const appointmentsPostMultiselectWithStall = useMemo(
    () =>
      filteredPendingAppointments.map(appt => ({
        ...appt,
        Stalled_Days:
          appt.Appointment_Status === 'In Progress'
            ? getStalledDaysSinceInProgress(appt.In_Progress_Date)
            : null
      })),
    [filteredPendingAppointments]
  );

  const visibleUntappedAgents = useMemo(() => {
    if (!filterAgencyIds) return untappedAgents;
    return Object.fromEntries(
      Object.entries(untappedAgents).filter(([, a]) =>
        filterAgencyIds.has(String(a.agencyId))
      )
    );
  }, [untappedAgents, filterAgencyIds]);

  const untappedAgentCount = useMemo(
    () => listUntappedAgents(visibleUntappedAgents).length,
    [visibleUntappedAgents]
  );

  const quickFilterCounts = useMemo(() => {
    const base = appointmentsPostMultiselectWithStall;
    const counts = AGENCY_APPOINTMENTS_QUICK_FILTERS.reduce(
      (acc, { id, match }) => {
        acc[id] = base.filter(match).length;
        return acc;
      },
      {}
    );
    counts[UNTAPPED_FILTER_ID] = untappedAgentCount;
    return counts;
  }, [appointmentsPostMultiselectWithStall, untappedAgentCount]);

  // each quick filter loads from its own source — untapped comes from useUntappedAgents, the
  // rest from usePendingAppointments — so their tab spinners resolve independently
  const quickFilterLoading = useMemo(() => {
    const map = {};
    for (const { id } of AGENCY_APPOINTMENTS_QUICK_FILTERS) {
      if (id === UNTAPPED_FILTER_ID) map[id] = untappedLoading;
      else if (id === RECOMMENDATIONS_FILTER_ID) map[id] = false;
      else map[id] = pendingLoading;
    }
    return map;
  }, [pendingLoading, untappedLoading]);

  const displayPendingAppointments = useMemo(() => {
    const def = AGENCY_APPOINTMENTS_QUICK_FILTERS.find(
      d => d.id === quickFilter
    );
    const matcher = def?.match ?? AGENCY_APPOINTMENTS_QUICK_FILTERS[0].match;
    return appointmentsPostMultiselectWithStall.filter(matcher);
  }, [appointmentsPostMultiselectWithStall, quickFilter]);

  const groupedPendingAppointmentsByAgentId = useMemo(() => {
    return displayPendingAppointments.reduce((acc, appt) => {
      const key = `${appt.agencyId || ''}::${appt.Agent_ID}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(appt);
      return acc;
    }, {});
  }, [displayPendingAppointments]);

  const pendingAgentsSorted = useMemo(() => {
    return Object.entries(groupedPendingAppointmentsByAgentId)
      .map(([compositeKey, appts]) => ({
        agentId: compositeKey,
        agentName: appts[0]?.Agent_Name?.trim() || String(appts[0]?.Agent_ID),
        agencyName: appts[0]?.agencyName ?? '',
        appointments: appts
      }))
      .sort(
        (a, b) =>
          (a.agencyName || '').localeCompare(b.agencyName || '') ||
          a.agentName.localeCompare(b.agentName)
      );
  }, [groupedPendingAppointmentsByAgentId]);

  const { isActive: isTourActive } = usePortalWalkthrough() || {};

  const tourMockAgents = useMemo(() => {
    if (!isTourActive || pendingAppointments.length > 0) return null;
    return [
      {
        agentId: '__tour_demo__',
        agentName: 'Alex Johnson',
        appointments: [
          {
            Agent_ID: '__tour_demo__',
            Agent_Name: 'Alex Johnson',
            Carrier_Name: 'Molina Healthcare',
            Appointment_Status: 'In Progress',
            Substatus: 'Submitted to Carrier',
            Appointment_Type: 'New Appointment',
            States: 'FL, TX',
            In_Progress_Date: new Date(
              Date.now() - 5 * 24 * 60 * 60 * 1000
            ).toISOString(),
            Ticket_ID: '__tour_mock_1__',
            Stalled_Days: 5
          }
        ]
      }
    ];
  }, [isTourActive, pendingAppointments.length]);

  useEffect(() => {
    setLocalLoader(false);
  }, []);

  useEffect(() => {
    if (!routeAgencyId) {
      navigate('/agencies');
      return;
    }
    getSpecificAgencyData(noopSetLocalLoader, routeAgencyId);
  }, [routeAgencyId, navigate, getSpecificAgencyData, noopSetLocalLoader]);

  // route change: drop the previous agency's data while the new meta loads
  const prevRouteRef = useRef(routeAgencyId);
  useEffect(() => {
    if (prevRouteRef.current === routeAgencyId) return;
    prevRouteRef.current = routeAgencyId;
    setSelectedAgencyOptions([]);
    markApplied([]);
  }, [routeAgencyId, markApplied]);

  useEffect(() => {
    if (!metaReady || agencyOptions.length === 0) return;
    if (initialLoadRef.current === routeAgencyId) return;
    initialLoadRef.current = routeAgencyId;
    const routeOption = agencyOptions.find(
      o => String(o.value) === String(routeAgencyId)
    );
    const initial = routeOption
      ? [routeOption]
      : [
          {
            name: agencyData?.agencyInformation?.name?._value ?? '',
            value: String(routeAgencyId)
          }
        ];
    setSelectedAgencyOptions(initial);
    markApplied(initial);
    quickFilterAutoAppliedAgencyIdRef.current = null;
  }, [metaReady, agencyOptions, routeAgencyId, markApplied, agencyData]);

  // filters default to everything selected — deselecting narrows the view
  useEffect(() => {
    setSelectedAgents(agentFilterOptions);
  }, [agentFilterOptions]);
  useEffect(() => {
    setSelectedCarriers(carrierFilterOptions);
  }, [carrierFilterOptions]);
  useEffect(() => {
    setSelectedStatuses(substatusFilterOptions);
  }, [substatusFilterOptions]);
  useEffect(() => {
    setAgencyFilterSelection(appliedOptions);
  }, [appliedOptions]);

  const updateDisabled =
    agencyMetaLoading ||
    pendingLoading ||
    untappedLoading ||
    selectedAgencyOptions.length === 0;

  const applySelection = useCallback(() => {
    apply();
    quickFilterAutoAppliedAgencyIdRef.current = null;
    setTimesInputClosed(prev => prev + 1);
  }, [apply]);

  const handleUpdate = useCallback(() => {
    if (updateDisabled) return;
    applySelection();
  }, [updateDisabled, applySelection]);

  const excludedFromExport = useMemo(
    () =>
      toAgencies(selectedAgencyOptions).filter(
        a => !appliedAgencyIds.includes(a.agencyId)
      ),
    [selectedAgencyOptions, appliedAgencyIds]
  );

  const handleContinueWithoutAgencies = useCallback(() => {
    setExportPendingModalOpen(false);
    exportPendingAppointments(PENDING_EXPORT_HEADERS);
  }, [exportPendingAppointments]);

  // the export needs the newly included agencies' rows before the render that
  // applies them, so it fetches them straight into the cache and exports the result
  const handleContinueWithSelected = useCallback(async () => {
    setExportPendingModalOpen(false);
    const agencies = toAgencies(selectedAgencyOptions);
    applySelection();
    const rows = await fetchPendingAppointments(agencies);
    if (rows?.length) exportPendingAppointments(PENDING_EXPORT_HEADERS, rows);
  }, [
    applySelection,
    selectedAgencyOptions,
    fetchPendingAppointments,
    exportPendingAppointments
  ]);

  useEffect(() => {
    if (pendingLoading || pendingAppointments.length === 0) return;
    // the URL tab wins — don't auto-pick when the user arrived on an explicit tab
    if (hasExplicitQuickFilter) return;
    const appliedKey = appliedAgencyIds.join(',');
    if (quickFilterAutoAppliedAgencyIdRef.current === appliedKey) return;
    quickFilterAutoAppliedAgencyIdRef.current = appliedKey;
    // Don't override specialty tabs across agency switches.
    if (
      quickFilter === RECOMMENDATIONS_FILTER_ID ||
      quickFilter === UNTAPPED_FILTER_ID
    ) {
      return;
    }
    setQuickFilter(pickQuickFilterIdByRecordPriority(quickFilterCounts));
  }, [
    pendingLoading,
    pendingAppointments.length,
    appliedAgencyIds,
    quickFilterCounts,
    quickFilter,
    hasExplicitQuickFilter,
    setQuickFilter
  ]);
  const handleConfirmExport = useCallback(
    includeInactive => {
      setExportModalOpen(false);
      downloadAppointmentsReport(
        setLoadingConfirmedAppointments,
        appliedAgencyIds,
        !includeInactive
      );
    },
    [downloadAppointmentsReport, appliedAgencyIds]
  );

  const allFailedAgencies = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const a of [...pendingFailedAgencies, ...untappedFailedAgencies]) {
      if (seen.has(a.agencyId)) continue;
      seen.add(a.agencyId);
      combined.push(a);
    }
    return combined;
  }, [pendingFailedAgencies, untappedFailedAgencies]);

  const handleRetryFailed = useCallback(async () => {
    await Promise.all([retryPendingFailed(), retryUntappedFailed()]);
  }, [retryPendingFailed, retryUntappedFailed]);

  const isRecommendationsView = quickFilter === RECOMMENDATIONS_FILTER_ID;
  const isUntappedView = quickFilter === UNTAPPED_FILTER_ID;

  const showAgentFilters =
    !isRecommendationsView &&
    !isUntappedView &&
    !pendingLoading &&
    pendingAppointments.length > 0;
  const showFiltersSection = appliedOptions.length > 1 || showAgentFilters;

  let pendingMainInner = null;
  if (filteredPendingAppointments.length === 0) {
    pendingMainInner = (
      <div className='flex flex-1 flex-col items-center justify-start py-8 text-center'>
        <p className='m-0 text-base font-semibold text-primary-darkened text-center'>
          No appointments match the current filters
        </p>
        <p className='mt-2 m-0 max-w-md text-sm text-grey text-center w-full'>
          Adjust the filters above or clear selections to see pending requests.
        </p>
      </div>
    );
  } else if (displayPendingAppointments.length === 0) {
    pendingMainInner = (
      <div className='flex flex-1 flex-col items-center justify-start py-8 text-center'>
        <p className='m-0 text-base font-semibold text-primary-darkened text-center'>
          No appointments in this view
        </p>
        <p className='mt-2 m-0 max-w-md text-sm text-grey text-center w-full'>
          Try another quick filter or broaden your filter selections.
        </p>
      </div>
    );
  } else {
    pendingMainInner = (
      <div className='flex flex-col gap-2 mt-4'>
        {pendingAgentsSorted.map(
          ({ agentId, agentName, agencyName, appointments }) => (
            <AgencyPendingAgentCard
              key={String(agentId)}
              agentId={String(agentId)}
              agentName={agentName}
              agencyName={agencyName}
              appointments={appointments}
            />
          )
        )}
      </div>
    );
  }

  let cardsContent = null;
  if (isRecommendationsView) {
    cardsContent = appliedAgencies.length > 0 && (
      <AgencyRecommendationsSection
        agencies={appliedAgencies}
        filterAgencyIds={filterAgencyIds}
      />
    );
  } else if (isUntappedView) {
    cardsContent = appliedAgencies.length > 0 && (
      <AgencyUntappedSection
        agencies={appliedAgencies}
        untappedAgents={visibleUntappedAgents}
        loading={untappedLoading}
        onAgentSuggested={removeUntappedAgent}
      />
    );
  } else if (!pendingLoading && tourMockAgents) {
    cardsContent = (
      <div className='flex flex-col gap-2 mt-4'>
        {tourMockAgents.map(({ agentId, agentName, appointments }) => (
          <AgencyPendingAgentCard
            key={agentId}
            agentId={agentId}
            agentName={agentName}
            appointments={appointments}
          />
        ))}
      </div>
    );
  } else if (!pendingLoading && pendingAppointments.length > 0) {
    cardsContent = pendingMainInner;
  } else {
    cardsContent = (
      <div className='flex h-full shrink-0 flex-col items-center justify-center bg-white p-4 mt-4 rounded-xl'>
        {(pendingLoading || agencyMetaLoading) && (
          <LoadingContent message='Loading pending appointments...' />
        )}
        {!pendingLoading &&
          !agencyMetaLoading &&
          pendingAppointments.length === 0 && (
            <div className='flex flex-col items-center justify-center'>
              <p className='text-xl font-semibold!'>No pending appointments</p>
              {!isMobileWebView() && (
                <Link to={`/agency/dashboard/${routeAgencyId}`}>
                  <Button
                    className='w-full sm:w-max'
                    variant='secondary'
                    size='small'
                    icon={<ChevronLeft />}
                    text='Go to Agency Dashboard'
                  />
                </Link>
              )}
            </div>
          )}
      </div>
    );
  }

  return (
    <div className='relative flex min-h-0 flex-1 flex-col h-full overflow-y-auto'>
      {routeAgencyId && !isMobileWebView() && (
        <Link className='mb-4' to={`/agency/dashboard/${routeAgencyId}`}>
          <Button
            className='w-full sm:w-max'
            variant='secondary'
            size='xsmall'
            icon={<ChevronLeft />}
            text='Go to Agency Dashboard'
          />
        </Link>
      )}
      <div className='flex flex-col gap-4 shrink-0 bg-white p-4 pb-0 rounded-xl border-b border-grey-lighter'>
        <div className='flex w-full shrink-0 flex-col gap-3 rounded-xl sm:flex-row sm:items-center sm:justify-between sm:gap-2 '>
          <h2 className='text-2xl font-semibold! m-0 leading-none'>
            Agency Appointment Center
          </h2>
          <div
            data-tour='appt-center-export'
            className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2'
          >
            <Button
              className='w-full sm:w-max'
              onClick={() => setExportModalOpen(true)}
              isLoading={loadingConfirmedAppointments}
              isDisabled={
                loadingConfirmedAppointments || appliedAgencyIds.length === 0
              }
              variant='primary'
              size='xsmall'
              icon={<Download />}
              text='Export Confirmed Appointments'
            />
            <Button
              className='w-full sm:w-max'
              onClick={() =>
                isDirty
                  ? setExportPendingModalOpen(true)
                  : exportPendingAppointments(PENDING_EXPORT_HEADERS)
              }
              isLoading={!loadingConfirmedAppointments && pendingLoading}
              isDisabled={
                pendingLoading ||
                loadingConfirmedAppointments ||
                pendingAppointments.length === 0
              }
              variant='primary'
              size='xsmall'
              icon={<Download />}
              text='Export Pending Appointments'
            />
          </div>
        </div>

        {routeAgencyId && (
          <AgencyMultiSelectBar
            id='appointments-agencies'
            options={agencyOptions}
            value={selectedAgencyOptions}
            onChange={setSelectedAgencyOptions}
            loading={agencyMetaLoading}
            isDirty={isDirty}
            onUpdate={handleUpdate}
            updateDisabled={updateDisabled}
            timesInputClosed={timesInputClosed}
          />
        )}

        {allFailedAgencies.length > 0 && !pendingLoading && !untappedLoading && (
          <div className='flex items-center gap-2 rounded-lg border border-semantic-danger/30 bg-semantic-danger/5 px-3 py-2'>
            <TriangleAlert className='size-4 shrink-0 text-semantic-danger' />
            <p className='m-0 flex-1 text-xs text-semantic-danger'>
              Failed to load data for:{' '}
              <span className='font-semibold'>
                {allFailedAgencies.map(a => a.agencyName).join(', ')}
              </span>
            </p>
            <Button
              variant='secondary'
              size='xsmall'
              text='Retry'
              onClick={handleRetryFailed}
              className='shrink-0'
            />
          </div>
        )}

        {routeAgencyId && showFiltersSection && (
          <div data-tour='appt-center-filters'>
            <h3 className='m-0 mb-2 text-base font-semibold text-primary-darkened'>
              Filters
            </h3>
            <AgencyAppointmentsFilters
              showAgencyPicker={appliedOptions.length > 1}
              agencyFilterOptions={appliedOptions}
              selectedAgencies={agencyFilterSelection}
              onAgenciesChange={setAgencyFilterSelection}
              showAgentFiltersRow={showAgentFilters}
              agentOptions={agentFilterOptions}
              carrierOptions={carrierFilterOptions}
              statusOptions={substatusFilterOptions}
              selectedAgents={selectedAgents}
              selectedCarriers={selectedCarriers}
              selectedStatuses={selectedStatuses}
              onAgentsChange={setSelectedAgents}
              onCarriersChange={setSelectedCarriers}
              onStatusesChange={setSelectedStatuses}
              disabled={pendingLoading || untappedLoading}
            />
          </div>
        )}
        <div className='flex w-full min-w-0 flex-col'>
          <div data-tour='appt-center-quick-filters'>
            <AgencyAppointmentsQuickFilters
              tabs={QUICK_FILTER_TABS}
              value={quickFilter}
              onChange={setQuickFilter}
              counts={quickFilterCounts}
              loadingByTab={quickFilterLoading}
            />
          </div>
        </div>
      </div>

      <div data-tour='appt-center-cards' className='flex flex-col gap-4'>
        {cardsContent}
      </div>

      <ExportConfirmedAppointmentsModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        agencies={appliedAgencies}
        onConfirm={handleConfirmExport}
        loading={loadingConfirmedAppointments}
        showUnappliedWarning={isDirty}
      />

      <ExportPendingAppointmentsModal
        open={exportPendingModalOpen}
        onClose={() => setExportPendingModalOpen(false)}
        excludedAgencies={excludedFromExport}
        onContinueWithout={handleContinueWithoutAgencies}
        onContinueWithSelected={handleContinueWithSelected}
        canContinueWithSelected={selectedAgencyOptions.length > 0}
      />
    </div>
  );
};

AgencyAppointments.propTypes = {
  setLocalLoader: propTypes.func,
  downloadAppointmentsReport: propTypes.func,
  agencyData: propTypes.object,
  getSpecificAgencyData: propTypes.func
};

export default AgencyAppointments;
